import {AfterViewInit, Component, inject, Input} from '@angular/core';
import {SearchObject} from "../../../core/definitions/search-object";
import {SearchContainer} from "../../../core/definitions/search-container";
import {FeatureFlagsService} from "../../../core/feature-flags.service";
import {SearchResultSelectionsService} from "../../search-result-selections.service";
import {MatCheckbox} from "@angular/material/checkbox";
import {NgClass} from "@angular/common";
import {RouterLink} from "@angular/router";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {SearchResultPackageIndicator} from "../search-result-package-indicator/search-result-package-indicator";
import {SearchResultThumbnailImage} from "../search-result-thumbnail/search-result-thumbnail-image";
import {RestrictedIndicatorComponent} from "../../../shared/restricted-indicator/restricted-indicator.component";

@Component({
  selector: 'app-search-result-view-gallery-v2',
  imports: [
    MatCheckbox,
    NgClass,
    RouterLink,
    MatTooltipModule,
    TranslateModule,
    SearchResultPackageIndicator,
    SearchResultThumbnailImage,
    RestrictedIndicatorComponent
  ],
  templateUrl: './search-result-view-gallery-v2.component.html',
  styleUrl: './search-result-view-gallery-v2.component.scss'
})
export class SearchResultViewGalleryV2Component implements AfterViewInit {
  // Injections
  private readonly featureFlagService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly searchResultSelectionService: SearchResultSelectionsService = inject(SearchResultSelectionsService);

  @Input() isRootObject: boolean;
  @Input() item: SearchObject;
  @Input() searchContainer: SearchContainer;
  needsDisableRefresh = new Set<string>();

  ngAfterViewInit() {
    if (this.isRootObject) {
      const element = document.getElementById(this.item.artifact_id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        element.focus();

        element.classList.add('gallery-item--highlighted');
        setTimeout(() => {
          element.classList.remove('gallery-item--highlighted');
        }, 3000);
      }
    }
  }

  isCheckboxDisabled(item: SearchObject): boolean {
    const itemId = item.artifact_id;
    const isSelected = this.searchContainer.selections.selected[itemId];

    if (this.needsDisableRefresh.has(itemId)) {
      this.needsDisableRefresh.delete(itemId);
      return item.$$used || (!isSelected && this.searchResultSelectionService.isSelectionLimitReached(this.searchContainer));
    }

    return item.$$used || (!isSelected && this.searchResultSelectionService.isSelectionLimitReached(this.searchContainer));
  }

  isCheckboxSelected(item: SearchObject): boolean {
    const itemId = item.artifact_id;
    if (this.isCheckboxDisabled(item) && !this.searchContainer.selections.selected[itemId]) {
      return false;
    }
    return this.searchContainer.selections.selected[itemId] || false;
  }

  getRouterLink(item: SearchObject) {
    if (item.$$refData?.routerLink?.join('/')?.includes('/artifact') && this.objectPageV2) {
      return [...item.$$refData.routerLink, item.artifact_id];
    }
    return item.$$refData?.routerLink
  }

  select(item: SearchObject, event: MouseEvent) {
    event.stopPropagation();
    const result = this.searchResultSelectionService.selectSearchResultItem(item, this.searchContainer, event.shiftKey);

    // Only prevent default if we have unselected items (hit the limit)
    if (event.shiftKey && result.unselectedItems.length > 0) {
      event.preventDefault();
    }

    // Mark all unselected items for refresh
    result.unselectedItems.forEach(unselectedItem => {
      this.needsDisableRefresh.add(unselectedItem.artifact_id);
    });
  }

  readonly objectPageV2 = this.featureFlagService.getFeatureFlags().experimental.useNewObjectPage;
}
